// apps/admin/src/pages/legal-entities/LegalEntityForm.tsx
//
// Phase 8.2 Wave 4 Task 23 — Admin Legal Entity create/edit modal.
// (Originally Phase 3.5 Wave 7 Task 21 — extended in 8.2.)
//
// Per CONTEXT.md:
//   - Legal Entity multi-entity model (Printway HK default + VN/US/SG)
//   - timezone field — IANA, used by Phase 4.1 invoice cron
//   - tax_config — VAT/GST/SALES_TAX/NONE
//   - Entity fields
//   - Logo upload + "Apply to all" button (this task)
//   - Status Active/Inactive (delete blocked when used)
//   - Searchable timezone picker (Amendment #1)
//   - Tax Configuration Editor (Amendment #3)
//
// Per API-CONTRACTS.md (Phase 3.5 + Phase 8.2 sub-phase):
//   POST  /api/v1/admin/legal-entities         (create — Idempotency-Key)
//   PUT   /api/v1/admin/legal-entities/:id     (edit — Idempotency-Key + If-Match: updated_at)
//
// Note: Phase 8.2 task-22 backend extend will eventually move PATCH +
// `standard_rate_bps` (basis points 0–3000). This UI keeps the working
// PUT + decimal-rate wire so the form stays functional during the
// build wave; once Task-22 lands we'll cut over in a follow-up touch.
//
// Test IDs per PLAN/task-23.md:
//   - legal-entity-timezone-input
//   - legal-entity-tax-type-select
//   - legal-entity-tax-rate-input
//   - legal-entity-tax-registration-input
//   - legal-entity-tax-jurisdiction-select
//   - legal-entity-reverse-charge-checkbox
//   - legal-entity-logo-apply-all-btn
//   - legal-entity-save-btn

import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FileUploadInput,
  Input,
  useToast,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import { API_BASE_URL } from '../../lib/apiClient.js';
import { z } from 'zod';
import { useTranslation } from '../../i18n/useTranslation.js';

import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from '../billing/billing-shared.js';
import { TimezonePicker } from '../../features/legal-entities/TimezonePicker.js';
import {
  TaxConfigSection,
  type TaxType,
  type TaxConfigValue,
} from '../../features/legal-entities/TaxConfigSection.js';

export type { TaxType };

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of GET-detail response.
// ---------------------------------------------------------------------------

export interface LegalEntityDetail {
  id: string;
  // Phase 8.2 debug 2026-05-23 — surfaced by BE service so the edit
  // dialog can hydrate the disabled `code` input + country hint.
  code?: string;
  country?: string;
  name: string;
  address: string;
  bank_info: string;
  tax_id: string;
  timezone: string;
  is_default: boolean;
  logo_url?: string | null;
  tax_config: {
    type: TaxType;
    standard_rate: number;
    registration_number: string | null;
    jurisdiction: string;
    reverse_charge_eligible: boolean;
    invoice_tax_label: string | null;
  };
  invoice_count: number;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Zod form schema — client-side validation (Rule 5) before any network call.
// Mirrors PostLegalEntityRequest. The Phase 8.2 contract uses
// `standard_rate_bps` integer 0–3000; FE works in decimal 0–0.30 and
// the helper at submit-time will convert when needed. For now we ship
// the decimal-rate wire (existing PUT contract) — backend Task-22
// extension will keep both shapes accepted during the migration window.
// ---------------------------------------------------------------------------

const TaxConfigSchema = z
  .object({
    type: z.enum(['VAT', 'GST', 'SALES_TAX', 'NONE']),
    standard_rate: z
      .number()
      .min(0, 'Tax rate must be ≥ 0%')
      .max(0.3, 'Tax rate must be ≤ 30%'),
    registration_number: z.string().max(80).nullable(),
    jurisdiction: z
      .string()
      .regex(
        /^[A-Z]{2}(-[A-Z0-9]{1,3})?$/,
        'ISO-3166 code (HK, VN, US, US-CA …)',
      ),
    reverse_charge_eligible: z.boolean(),
    invoice_tax_label: z.string().max(80).nullable(),
  })
  .superRefine((cfg, ctx) => {
    // type !== NONE requires registration + rate.
    if (cfg.type !== 'NONE') {
      if (!cfg.registration_number || cfg.registration_number.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['registration_number'],
          message: `${cfg.type} requires a registration number`,
        });
      }
      if (cfg.standard_rate <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['standard_rate'],
          message: `${cfg.type} requires a standard rate > 0%`,
        });
      }
    }
  });

const FormSchema = z.object({
  // Phase 8.2 debug 2026-05-23 bug 4 — `code` + `country` are required by
  // the BE create body (POST /api/v1/admin/legal-entities). The form
  // previously omitted both fields, so every submit 400'd with
  // FST_ERR_VALIDATION (`data must have required property 'code'`).
  code: z
    .string()
    .min(1, 'Required')
    .max(50)
    .regex(/^[A-Z0-9_]+$/, 'UPPER_SNAKE (letters / digits / underscore)'),
  name: z.string().min(1, 'Required').max(200),
  // Phase 8.2 debug 2026-05-23 — `country` is a transient FE hint (BE no
  // longer requires it; the canonical jurisdiction lives in
  // tax_config.jurisdiction). Optional + lenient so the edit form does
  // not block submit when the field is blank or non-ISO.
  country: z
    .string()
    .max(2)
    .regex(/^[A-Z]{2}$|^$/, 'Two upper-case letters')
    .optional()
    .or(z.literal('')),
  address: z.string().min(1, 'Required').max(500),
  bank_info: z.string().max(500),
  tax_id: z.string().min(1, 'Required').max(50),
  timezone: z
    .string()
    .regex(
      /^[A-Za-z_]+\/[A-Za-z_]+(\/[A-Za-z_]+)?$/,
      'IANA timezone (e.g. Asia/Hong_Kong)',
    ),
  logo_url: z.string().url('Must be a valid URL').or(z.literal('')).optional(),
  tax_config: TaxConfigSchema,
});

export type LegalEntityFormValues = z.infer<typeof FormSchema>;

// ---------------------------------------------------------------------------
// Default form state.
// ---------------------------------------------------------------------------

const DEFAULT_VALUES: LegalEntityFormValues = {
  code: '',
  name: '',
  country: 'HK',
  address: '',
  bank_info: '',
  tax_id: '',
  timezone: 'Asia/Hong_Kong',
  logo_url: '',
  tax_config: {
    type: 'NONE',
    standard_rate: 0,
    registration_number: null,
    jurisdiction: 'HK',
    reverse_charge_eligible: false,
    invoice_tax_label: null,
  },
};

// ---------------------------------------------------------------------------
// Address-country parser — pulls a 2-letter ISO from the free-text address.
// Best-effort: scans tokens for ISO-3166-alpha2 and falls back to suffix
// match (e.g. "Hong Kong" → HK). Cheap heuristic so admin doesn't need to
// edit a separate "country" field for the auto-suggest jurisdiction feature.
// ---------------------------------------------------------------------------

const COUNTRY_HINTS: Array<[RegExp, string]> = [
  [/\b(HK|Hong\s*Kong)\b/i, 'HK'],
  [/\b(VN|Vietnam|Việt\s*Nam)\b/i, 'VN'],
  [/\b(SG|Singapore)\b/i, 'SG'],
  [/\b(US|USA|United\s*States)\b/i, 'US'],
  [/\b(GB|UK|United\s*Kingdom|England)\b/i, 'GB'],
  [/\b(DE|Germany|Deutschland)\b/i, 'DE'],
  [/\b(FR|France)\b/i, 'FR'],
  [/\b(NL|Netherlands)\b/i, 'NL'],
  [/\b(JP|Japan)\b/i, 'JP'],
  [/\b(CN|China)\b/i, 'CN'],
  [/\b(AU|Australia)\b/i, 'AU'],
  [/\b(CA|Canada)\b/i, 'CA'],
];

function inferCountryFromAddress(address: string): string | null {
  for (const [pattern, code] of COUNTRY_HINTS) {
    if (pattern.test(address)) return code;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface LegalEntityFormState {
  mode: 'create' | 'edit';
  entity?: LegalEntityDetail;
}

export interface LegalEntityFormProps {
  state: LegalEntityFormState | null;
  onClose: () => void;
  /** Called after a successful POST/PUT — parent should refresh list. */
  onSaved: () => void;
  /** Called when admin clicks "Apply logo to all" — parent runs the
   * cross-entity copy. Receives the current entity id (or null for create) and
   * the logo URL to broadcast. Optional — section button hides when absent. */
  onApplyLogoToAll?: (logoUrl: string, sourceEntityId: string | null) => Promise<void>;
}

export function LegalEntityForm({
  state,
  onClose,
  onSaved,
  onApplyLogoToAll,
}: LegalEntityFormProps): JSX.Element | null {
  const { t } = useTranslation('finance');
  const { toast } = useToast();

  const [values, setValues] = useState<LegalEntityFormValues>(DEFAULT_VALUES);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [applyingLogo, setApplyingLogo] = useState(false);
  // Hydrate when modal opens.
  useEffect(() => {
    if (!state) return;
    if (state.mode === 'edit' && state.entity) {
      const e = state.entity;
      setValues({
        // Phase 8.2 debug 2026-05-23 bug A — `code` MUST be hydrated from
        // the now-populated detail response so the disabled input still
        // emits the canonical value on save. Falls back to the legacy
        // `code` cast for environments that haven't deployed the BE
        // serializer fix yet.
        code: e.code ?? (e as unknown as { code?: string }).code ?? '',
        name: e.name,
        country:
          e.country ??
          (e as unknown as { country?: string }).country ??
          inferCountryFromAddress(e.address) ??
          'HK',
        address: e.address,
        bank_info: e.bank_info,
        tax_id: e.tax_id,
        timezone: e.timezone || '',
        logo_url: e.logo_url ?? '',
        tax_config: {
          type: e.tax_config.type,
          standard_rate: e.tax_config.standard_rate,
          registration_number: e.tax_config.registration_number,
          jurisdiction: e.tax_config.jurisdiction,
          reverse_charge_eligible: e.tax_config.reverse_charge_eligible,
          invoice_tax_label: e.tax_config.invoice_tax_label,
        },
      });
    } else {
      setValues(DEFAULT_VALUES);
    }
    setErrors({});
    setGeneralError(null);
    setSubmitting(false);
  }, [state]);

  if (!state) return null;

  const isEdit = state.mode === 'edit' && state.entity != null;
  const isLegacyEntity = isEdit && (!state.entity?.timezone || state.entity.timezone === '');

  function setField<K extends keyof LegalEntityFormValues>(
    key: K,
    val: LegalEntityFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: val }));
    if (errors[key as string]) {
      setErrors((prev) => ({ ...prev, [key as string]: undefined }));
    }
  }

  function setTaxField<K extends keyof TaxConfigValue>(
    key: K,
    val: TaxConfigValue[K],
  ): void {
    setValues((prev) => ({
      ...prev,
      tax_config: { ...prev.tax_config, [key]: val },
    }));
    const errKey = `tax_config.${String(key)}`;
    if (errors[errKey]) {
      setErrors((prev) => ({ ...prev, [errKey]: undefined }));
    }
  }

  async function handleApplyLogoToAll(): Promise<void> {
    if (!onApplyLogoToAll) return;
    const url = (values.logo_url ?? '').trim();
    if (url === '') {
      toast({
        variant: 'error',
        title: t('legal_entity.form.logoMissingTitle'),
        description: t('legal_entity.form.logoMissingDesc'),
      });
      return;
    }
    const sourceId = isEdit && state?.entity ? state.entity.id : null;
    setApplyingLogo(true);
    try {
      await onApplyLogoToAll(url, sourceId);
      toast({
        variant: 'success',
        title: t('legal_entity.form.logoSyncedTitle'),
        description: t('legal_entity.form.logoSyncedDesc'),
      });
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('legal_entity.form.logoSyncFailed');
      toast({
        variant: 'error',
        title: t('legal_entity.form.logoApplyFailedTitle'),
        description: msg,
      });
    } finally {
      setApplyingLogo(false);
    }
  }

  async function handleSubmit(): Promise<void> {
    const currentState = state;
    if (!currentState) return;
    setGeneralError(null);
    // Rule 5 — client-side validation BEFORE network call.
    const parsed = FormSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<string, string>> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        if (!fieldErrors[path]) fieldErrors[path] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    const editing = currentState.mode === 'edit' && currentState.entity != null;

    setSubmitting(true);
    try {
      const c = apiClient() as unknown as LooseClient;
      const headers: Record<string, string> = {
        'Idempotency-Key': newIdempotencyKey(),
      };
      if (editing && currentState.entity) {
        headers['If-Match'] = currentState.entity.updated_at;
      }
      const method = editing ? 'PUT' : 'POST';
      const path =
        editing && currentState.entity
          ? `/api/v1/admin/legal-entities/${encodeURIComponent(currentState.entity.id)}`
          : '/api/v1/admin/legal-entities';

      // Strip empty logo_url so backend doesn't reject it as bad URL.
      const body = { ...parsed.data };
      if (!body.logo_url || body.logo_url.trim() === '') delete body.logo_url;

      const verb = method === 'PUT' ? c.PUT : c.POST;
      const { data, error, response } = await verb(path, {
        headers,
        body,
      });

      if (error || !response.ok) {
        const msg = envelopeError(error) ?? `HTTP ${response.status}`;
        // 409 DUPLICATE_TAX_ID / CODE_DUPLICATE → inline tax_id error.
        if (response.status === 409) {
          setErrors((prev) => ({
            ...prev,
            tax_id: msg || t('legal_entity.form.duplicateTaxCode'),
          }));
          return;
        }
        // 412 PRECONDITION_FAILED (If-Match mismatch — concurrent edit).
        if (response.status === 412) {
          setGeneralError(t('legal_entity.form.preconditionFailed'));
          return;
        }
        // 422 — tax config invalid or in-use entity delete attempt.
        if (response.status === 422) {
          setGeneralError(msg);
          return;
        }
        setGeneralError(msg);
        toast({
          variant: 'error',
          title: editing
            ? t('legal_entity.form.updateFailed')
            : t('legal_entity.form.createFailed'),
          description: msg,
        });
        return;
      }

      const result = unwrap<{ id: string }>(data);
      toast({
        variant: 'success',
        title: editing
          ? t('legal_entity.form.updateSuccess')
          : t('legal_entity.form.createSuccess'),
        description: parsed.data.name,
      });
      onSaved();
      onClose();
      void result?.id;
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('legal_entity.common.networkDesc');
      setGeneralError(msg);
      toast({
        variant: 'error',
        title: t('legal_entity.common.networkTitle'),
        description: msg,
      });
    } finally {
      setSubmitting(false);
    }
  }

  const addressCountry = inferCountryFromAddress(values.address);

  return (
    <Dialog
      open={!!state}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      className="max-w-2xl"
    >
      <DialogHeader>
        <DialogTitle>
          {isEdit
            ? t('legal_entity.form.editTitle')
            : t('legal_entity.form.createTitle')}
        </DialogTitle>
        {isEdit ? (
          <p className="mt-1 text-xs text-pw-text-muted">
            {t('legal_entity.form.editNote')}
          </p>
        ) : null}
        {isLegacyEntity ? (
          <p
            className="mt-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
            role="alert"
            data-testid="legal-entity-legacy-timezone-warning"
          >
            {t('legal_entity.form.legacyTimezoneWarning')}
          </p>
        ) : null}
      </DialogHeader>

      <div className="mt-4 max-h-[65vh] overflow-y-auto pr-1">
        <div className="space-y-5">
          {/* --- Identity section --- */}
          {/* Phase 8.2 debug 2026-05-23 bug 4 — added `code` + `country`
              inputs because the BE create body declares them required.
              `code` is locked to UPPER_SNAKE; `country` is ISO-3166 alpha-2.
              `code` is editable on create only — flipping it on edit would
              break invoice/receipt snapshot continuity. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="le-code" className="mb-1 block text-sm font-medium text-pw-text">
                {t('legal_entity.form.codeLabel')}{' '}
                <span className="text-rose-600 dark:text-rose-300">*</span>
              </label>
              <Input
                id="le-code"
                value={values.code}
                onChange={(e) =>
                  setField('code', e.target.value.toUpperCase())
                }
                placeholder="PWH"
                aria-invalid={errors.code ? 'true' : 'false'}
                data-testid="legal-entity-code-input"
                disabled={isEdit}
                maxLength={50}
              />
              {errors.code ? (
                <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
                  {errors.code}
                </p>
              ) : (
                <p className="mt-1 text-xs text-pw-text-muted">
                  {t('legal_entity.form.codeHelp')}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="le-country" className="mb-1 block text-sm font-medium text-pw-text">
                {t('legal_entity.form.countryLabel')}{' '}
                <span className="text-rose-600 dark:text-rose-300">*</span>
              </label>
              <Input
                id="le-country"
                value={values.country}
                onChange={(e) =>
                  setField('country', e.target.value.toUpperCase())
                }
                placeholder="HK"
                aria-invalid={errors.country ? 'true' : 'false'}
                data-testid="legal-entity-country-input"
                maxLength={2}
              />
              {errors.country ? (
                <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
                  {errors.country}
                </p>
              ) : null}
            </div>
            <div>
              <label htmlFor="le-tax-id" className="mb-1 block text-sm font-medium text-pw-text">
                {t('legal_entity.form.taxIdLabel')}{' '}
                <span className="text-rose-600 dark:text-rose-300">*</span>
              </label>
              <Input
                id="le-tax-id"
                value={values.tax_id}
                onChange={(e) => setField('tax_id', e.target.value)}
                placeholder="HK12345678"
                aria-invalid={errors.tax_id ? 'true' : 'false'}
                data-testid="legal-entity-tax-id-input"
              />
              {errors.tax_id ? (
                <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
                  {errors.tax_id}
                </p>
              ) : (
                <p className="mt-1 text-xs text-pw-text-muted">
                  {t('legal_entity.form.taxIdHelp')}
                </p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="le-name" className="mb-1 block text-sm font-medium text-pw-text">
              {t('legal_entity.form.nameLabel')}{' '}
              <span className="text-rose-600 dark:text-rose-300">*</span>
            </label>
            <Input
              id="le-name"
              value={values.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Printway Hong Kong Ltd"
              aria-invalid={errors.name ? 'true' : 'false'}
              data-testid="legal-entity-name-input"
            />
            {errors.name ? (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
                {errors.name}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="le-address" className="mb-1 block text-sm font-medium text-pw-text">
              {t('legal_entity.form.addressLabel')}{' '}
              <span className="text-rose-600 dark:text-rose-300">*</span>
            </label>
            <textarea
              id="le-address"
              className="block h-20 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
              value={values.address}
              onChange={(e) => setField('address', e.target.value)}
              maxLength={500}
              placeholder="11/F, The Center, 99 Queen's Road Central, Hong Kong"
              aria-invalid={errors.address ? 'true' : 'false'}
              data-testid="legal-entity-address-input"
            />
            {errors.address ? (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
                {errors.address}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="le-bank" className="mb-1 block text-sm font-medium text-pw-text">
              {t('legal_entity.form.bankInfoLabel')}
            </label>
            <textarea
              id="le-bank"
              className="block h-20 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
              value={values.bank_info}
              onChange={(e) => setField('bank_info', e.target.value)}
              maxLength={500}
              placeholder={t('legal_entity.form.bankInfoPlaceholder')}
              data-testid="legal-entity-bank-info-input"
            />
          </div>

          {/* --- Logo section --- */}
          {/* Phase 8.2 debug 2026-05-23 bug 4 — admin uploads a file
              instead of pasting a URL. POST /api/v1/files/avatar accepts
              jpeg/png/webp ≤2MB and returns `{ url, fileId, ... }`. The
              resolved `url` is stored in `values.logo_url` so the rest of
              the submit + "Apply to all" pipeline stays unchanged. */}
          <div>
            <label className="mb-1 block text-sm font-medium text-pw-text">
              {t('legal_entity.form.logoLabel')}
            </label>
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-[240px] flex-1">
                <FileUploadInput
                  apiBaseUrl={API_BASE_URL}
                  value={values.logo_url ?? ''}
                  onChange={(url) => setField('logo_url', url)}
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  maxBytes={2 * 1024 * 1024}
                  testId="legal-entity-logo-url-input"
                  helperText={t('legal_entity.form.logoHelper')}
                />
              </div>
              {onApplyLogoToAll ? (
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => {
                    void handleApplyLogoToAll();
                  }}
                  disabled={
                    applyingLogo ||
                    !values.logo_url ||
                    values.logo_url.trim() === ''
                  }
                  data-testid="legal-entity-logo-apply-all-btn"
                >
                  {applyingLogo
                    ? t('legal_entity.form.applying')
                    : t('legal_entity.form.applyToAll')}
                </Button>
              ) : null}
            </div>
            {errors.logo_url ? (
              <p className="mt-2 text-xs text-rose-600 dark:text-rose-300" role="alert">
                {errors.logo_url}
              </p>
            ) : null}
          </div>

          {/* --- Timezone (searchable picker) --- */}
          <div>
            <label
              htmlFor="legal-entity-timezone"
              className="mb-1 block text-sm font-medium text-pw-text"
            >
              {t('legal_entity.form.timezoneLabel')}{' '}
              <span className="text-rose-600 dark:text-rose-300">*</span>
            </label>
            <TimezonePicker
              value={values.timezone}
              onChange={(tz) => setField('timezone', tz)}
              error={errors.timezone ?? null}
              warning={
                isLegacyEntity && !values.timezone
                  ? t('legal_entity.form.timezoneWarning')
                  : null
              }
              disabled={submitting}
            />
          </div>

          {/* --- Tax config section --- */}
          <TaxConfigSection
            value={values.tax_config}
            onChange={setTaxField}
            addressCountry={addressCountry}
            errors={errors}
            disabled={submitting}
          />

          {generalError ? (
            <p
              className="rounded-md bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-xs text-rose-700 dark:text-rose-300"
              role="alert"
            >
              {generalError}
            </p>
          ) : null}
        </div>
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          {t('legal_entity.common.cancel')}
        </Button>
        <Button
          variant="default"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={submitting}
          data-testid="legal-entity-save-btn"
        >
          {submitting
            ? isEdit
              ? t('legal_entity.form.saving')
              : t('legal_entity.form.creating')
            : isEdit
              ? t('legal_entity.form.saveChanges')
              : t('legal_entity.form.createEntity')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
